import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  type?: 'website' | 'article' | 'software';
}

export function SEOHead({ 
  title, 
  description, 
  keywords, 
  ogImage = '/og-image.png',
  canonical,
  type = 'website'
}: SEOHeadProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta description
    updateMetaTag('description', description);
    
    // Update keywords if provided
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update Open Graph tags
    updateMetaProperty('og:title', title);
    updateMetaProperty('og:description', description);
    updateMetaProperty('og:type', type === 'software' ? 'website' : type);
    updateMetaProperty('og:image', ogImage);
    
    // Update Twitter Card tags
    updateMetaProperty('twitter:title', title);
    updateMetaProperty('twitter:description', description);
    updateMetaProperty('twitter:image', ogImage);
    updateMetaProperty('twitter:card', 'summary_large_image');

    // Update canonical URL if provided
    if (canonical) {
      updateCanonicalLink(canonical);
    }

    // Update structured data
    const schemaData: any = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': 'https://llmtext.net/#organization',
          'name': 'llm.txt Generator',
          'url': 'https://llmtext.net/',
          'logo': {
            '@type': 'ImageObject',
            'url': 'https://llmtext.net/favicon.svg'
          }
        },
        {
          '@type': 'WebSite',
          '@id': 'https://llmtext.net/#website',
          'url': 'https://llmtext.net/',
          'name': 'llm.txt Generator',
          'publisher': { '@id': 'https://llmtext.net/#organization' }
        }
      ]
    };

    if (type === 'article') {
      schemaData['@graph'].push({
        '@type': 'Article',
        'headline': title,
        'description': description,
        'image': ogImage,
        'author': { '@id': 'https://llmtext.net/#organization' },
        'publisher': { '@id': 'https://llmtext.net/#organization' }
      });
    } else if (type === 'software') {
      schemaData['@graph'].push({
        '@type': 'WebApplication',
        'name': title,
        'description': description,
        'applicationCategory': 'BusinessApplication',
        'operatingSystem': 'Any',
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'USD'
        }
      });
    }

    updateStructuredData(schemaData);
  }, [title, description, keywords, ogImage, canonical, type]);

  return null; // This component doesn't render anything
}

function updateMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function updateMetaProperty(property: string, content: string) {
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function updateCanonicalLink(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function updateStructuredData(data: any) {
  // Remove existing structured data
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) {
    existing.remove();
  }

  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}
